import _ from 'lodash';
import { useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';

const concatFiles = (fileList1, fileList2) => {
    const dataTransferList = new DataTransfer(),
        existingFileSet = new Set();
    if (fileList1?.length > 0) {
        for (const file of fileList1) {
            existingFileSet.add(file.name);
            dataTransferList.items.add(file);
        }
    }
    if (fileList2?.length > 0) {
        for (const file of fileList2) {
            if (!existingFileSet.has(file.name)) {
                dataTransferList.items.add(file);
            }
        }
    }
    return dataTransferList.files;
};

const Upload = ({
    id,
    name,
    label,
    required,
    placeholder,
    isMulti,
    accept,
    validateFileType,
    shouldUnregister,
    appendFiles,
    className = '',
    maxSizeBytes,
    renderFileUpload,
    renderFileDisplay,
    rednderClass,
    errorClassName = 'text-scogoclosed text-font08 font-normal',
    readOnly,
    disabled = false,
    uploadText = 'No File Chosen',
    onChange: onFileUpload = () => { },
    padding = 'px-2 py-2 ',
    labelClass,
    ref,
    hiddenClass = '',
    showText,
    existingFileUrl
}) => {
    if (accept && !validateFileType)
        throw new Error(
            'validateFileType is compulsory if you are using accept because even with accept it is possible to upload any file type, so actual validation is necessary'
        );
    const {
        formState: { errors },
        control,
    } = useFormContext();
    const fileRef = useRef();
    const triggerUpload = () => fileRef?.current?.click();

    const error = _.get(errors, name) ? _.get(errors, name).message : '';

    const handleViewFile = (file) => {
        const url = URL.createObjectURL(file);
        window.open(url, '_blank');
    };

    return (
        <div
            className={`block ${padding} ${className}`}
            style={{
                minHeight: 0,
                minWidth: 0,
            }}
            ref={ref}
        >
            {label && (
                <label className={`text-scogo99 font-normal text-font09 md:text-font11 labelFotnSize`}>
                    {label} {required && <span className='text-scogoorange'>*</span>}
                </label>
            )}
            <Controller
                key={name}
                control={control}
                name={name}
                rules={{
                    required: { value: required, message: 'Required' },
                    validate: {
                        invalidFileType: (files) => {
                            if (!validateFileType || !files || files?.length === 0) return true;
                            let invalidFileMessage;
                            for (const file of files) {
                                const res = validateFileType(file);
                                if (typeof res === 'string') {
                                    invalidFileMessage = res;
                                    break;
                                }
                            }
                            return !invalidFileMessage || invalidFileMessage;
                        },
                        fileLargerThanLimit: (files) => {
                            if (!maxSizeBytes || !files || files?.length === 0) return true;
                            const limitBreachingFile = [...files].find((file) => file.size > maxSizeBytes);
                            if (!limitBreachingFile) return true;
                            return `${limitBreachingFile.name} is more than ${maxSizeBytes / 1000} KB`;
                        },
                    },
                }}
                shouldUnregister={shouldUnregister}
                render={({ field: { onChange, onBlur, value: files, name, ref } }) => {
                    // A saved upload can be represented by its URL in form state.
                    // Treat it as a URL, never as an iterable list of File objects.
                    const isUrlValue = typeof files === 'string';
                    const selectedFiles = isUrlValue ? undefined : files;
                    const savedFileUrl = existingFileUrl || (isUrlValue ? files : undefined);
                    const removeFile = (file) => {
                        const dataTransferList = new DataTransfer();
                        fileRef.current.value = '';
                        for (const f of selectedFiles || []) {
                            if (file.name !== f.name) dataTransferList.items.add(f);
                        }
                        onChange(dataTransferList.files.length > 0 ? dataTransferList.files : undefined);
                        fileRef.current.value = '';
                    };
                    return (
                        <div className={rednderClass}>
                            {renderFileUpload ? (
                                renderFileUpload({ files: selectedFiles, triggerUpload })
                            ) : (
                                <div
                                    className={`flex justify-between  items-center w-full ${labelClass} inputFontSize ${disabled ? 'border-b-0 cursor-not-allowed' : 'border-b-2 border-scogoddd'}`}
                                    style={{ height: '2.9rem' }}
                                    onClick={triggerUpload}
                                    type='button'
                                >
                                    {showText && <div className='truncate'>  {!selectedFiles || selectedFiles.length === 0 ? uploadText : selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length} Files`}
                                    </div>}
                                    <span className='material-icons text-font16'>attach_file</span>
                                </div>
                            )}
                            {renderFileDisplay ? (
                                renderFileDisplay({ files: selectedFiles, removeFile })
                            ) : (
                                <div className='flex items-center gap-2 flex-wrap'>
                                    {selectedFiles &&
                                        [...selectedFiles].map((file) => (
                                            <div className='flex items-center' key={file.name} title={file.name}>
                                                <div className={`max-w-6 truncate font-normal text-font09 md:text-font11 text-scogoprimary inputFontSize`}>{file.name}</div>
                                                <span className='text-font08 bg-white material-icons cursor-pointer hover:text-scogoorange ' onClick={() => removeFile(file)}>
                                                    clear
                                                </span>
                                                <button
                                                    type="button"
                                                    className="text-font08 bg-white material-icons cursor-pointer hover:text-scogoorange"
                                                    onClick={() => handleViewFile(file)}
                                                >
                                                    visibility
                                                </button>
                                            </div>
                                        ))}
                                    {savedFileUrl && (!selectedFiles || selectedFiles.length === 0) && (
                                        <a
                                            className='max-w-full truncate font-normal text-font09 md:text-font11 text-scogoprimary inputFontSize'
                                            href={savedFileUrl}
                                            target='_blank'
                                            rel='noreferrer'
                                            title={savedFileUrl}
                                        >
                                            {savedFileUrl.split('/').pop().split('?')[0]}
                                        </a>
                                    )}
                                </div>
                            )}
                            <input
                                id={id}
                                key={name}
                                ref={fileRef}
                                disabled={disabled}
                                type='file'
                                style={{ height: '3rem' }}
                                className={`hidden inputFontSize ${hiddenClass} `}
                                onChange={(e) => {
                                    const newFiles = appendFiles ? concatFiles(selectedFiles, e.target.files) : e.target.files;
                                    onChange(newFiles);
                                    onFileUpload(newFiles);
                                }}
                                onBlur={(e) => {
                                    onBlur(e.target.files);
                                }}
                                placeholder={placeholder}
                                multiple={isMulti}
                                accept={accept}
                                readOnly={readOnly}
                            />
                        </div>
                    );
                }}
            />
            {required && error && <p className='text-scogoclosed text-font08 font-normal inputFontSize h-6'>{error}</p>}
        </div>
    );
};

export default Upload;
